import type { Booking } from '@brighterly/lib-core-types/booking/interfaces'
import type { BookingStore } from './interfaces'
import type { ScopeMode } from '@utils/stores/scoped-entry'

import { useDisciplineStore } from '@stores/discipline'

import { useBookingsApi } from '@api/bookings/index.hook'
import { useFinishedBookingsApi } from '@api/bookings/finished.hook'
import { useCurrentBookingApi } from '@api/bookings/current.hook'

import { beginEntry, createScopedEntry, failEntry, settleEntry } from '@utils/stores/scoped-entry'

import { createInitialState } from './state'

export const actions = {
  resetState (this: BookingStore): void {
    Object.assign(this, createInitialState())
  },
  beginCurrent (this: BookingStore, id: number, mode: ScopeMode): number {
    return beginEntry(this.currentById, id, mode)
  },
  settleCurrent (this: BookingStore, id: number, requestId: number, data: Booking | null): void {
    settleEntry(this.currentById, id, requestId, data)
  },
  failCurrent (this: BookingStore, id: number, requestId: number): void {
    failEntry(this.currentById, id, requestId)
  },
  patchCurrent (this: BookingStore, id: number, booking: Booking | null): void {
    const entry = this.currentById[id] ?? createScopedEntry<Booking | null>()
    this.currentById[id] = entry
    entry.data = booking
  },
  beginList (this: BookingStore, id: number, mode: ScopeMode): number {
    return beginEntry(this.listById, id, mode)
  },
  settleList (this: BookingStore, id: number, requestId: number, data: Booking[]): void {
    settleEntry(this.listById, id, requestId, data)
  },
  failList (this: BookingStore, id: number, requestId: number): void {
    failEntry(this.listById, id, requestId)
  },
  beginFinished (this: BookingStore, id: number, mode: ScopeMode): number {
    return beginEntry(this.finishedById, id, mode)
  },
  settleFinished (this: BookingStore, id: number, requestId: number, data: Booking[]): void {
    settleEntry(this.finishedById, id, requestId, data)
  },
  failFinished (this: BookingStore, id: number, requestId: number): void {
    failEntry(this.finishedById, id, requestId)
  },
  setCountdown (this: BookingStore, payload: BookingStore['countdown']): void {
    this.countdown = payload
  },
  async refreshNow (this: BookingStore, payload: { background: boolean }): Promise<void> {
    const disciplineId = useDisciplineStore().activeDiscipline?.id
    if (!disciplineId) return

    await useBookingsApi({
      disciplineId,
      background: payload.background,
    })
  },
  async refreshCurrentNow (this: BookingStore, payload: { background: boolean }): Promise<void> {
    const disciplineId = useDisciplineStore().activeDiscipline?.id
    if (!disciplineId) return

    await useCurrentBookingApi({
      disciplineId,
      background: payload.background,
    })
  },
  async refreshFinishedNow (this: BookingStore, payload: { background: boolean }): Promise<void> {
    const disciplineId = useDisciplineStore().activeDiscipline?.id
    if (!disciplineId) return

    await useFinishedBookingsApi({
      disciplineId,
      background: payload.background,
    })
  },
  setModalJoinDemoLesson (this: BookingStore, payload: BookingStore['modalJoinDemoLesson']): void {
    this.modalJoinDemoLesson = payload
  },
  setModalJoinPaidLesson (this: BookingStore, payload: BookingStore['modalJoinPaidLesson']): void {
    this.modalJoinPaidLesson = payload
  },
  setBookingTrigger (this: BookingStore): void {
    this.bookingTrigger++
  },
  setIcsLink (this: BookingStore, payload: BookingStore['icsLink']): void {
    this.icsLink = payload
  },
  setIcsProcessing (this: BookingStore, payload: BookingStore['icsProcessing']): void {
    this.icsProcessing = payload
  },
}
